import {AfterViewInit, ChangeDetectorRef, Component, Input, OnInit, QueryList, ViewChildren} from '@angular/core';
import {
    Actor,
    BaseTimelineNotificationProvider,
    Circuit,
    ComponentSetting,
    DefaultComponentSettingService,
    PesDossier,
    PesDossierService,
    PesTaskStatus,
    TimelineNotificationContainerDirective
} from '@pes/ui';
import {TranslateService} from '@ngx-translate/core';

@Component({
    selector: 'app-lbpa-timeline',
    templateUrl: './lbpa-timeline.component.html'
})
export class LbpaTimelineComponent implements OnInit, AfterViewInit {

    /**
     * The reference to the circuit for which to show the timeline.
     */
    @Input() public circuit: Circuit = null;

    /**
     * The container to inject the list of step notifications.
     */
    @ViewChildren(TimelineNotificationContainerDirective)
    protected containerNotifications: QueryList<TimelineNotificationContainerDirective>;

    /**
     * The map of actors associated with the circuit.
     */
    protected actors: { [id: string]: Actor; } = {};

    /**
     * The current step index.
     */
    protected stepIdx: number = -1;

    /**
     * A boolean to hide or not ignored steps (true to hide).
     *
     */
    private _hideIgnoredTasks: boolean = false;

    /**
     * The list of tasks not displayed within the Timeline component, based on their IDs.
     */
    private _excludedTasks: string[] = null;

    /**
     * Creates a new PesTimelineComponent instance.
     *
     * @param {TranslateService} _translate the service used for translations
     * @param {BaseTimelineNotificationProvider} _notification the service used to create timeline notification
     * @param {DefaultComponentSettingService} _componentSetting the service used to fetch settings for circuit components.
     * @param {ChangeDetectorRef} _cdr the service used to detect a change
     */
    constructor(private readonly _translate: TranslateService,
                private readonly _notification: BaseTimelineNotificationProvider,
                private readonly _componentSetting: DefaultComponentSettingService,
                private readonly _cdr: ChangeDetectorRef,
                private readonly _dossierService: PesDossierService) {
    }

    /**
     * @inheritDoc
     */
    public ngOnInit(): void {
        this.setSettings();
        const dossier: PesDossier = this.circuit.dossier;
        this.circuit.actors.forEach(actor => this.actors[actor.id] = actor);
        if (dossier) {
            dossier.taskList.forEach(task => {
                if (task.data.id === this.circuit.workflow.currentTask.id) {
                    this.stepIdx = task.data.stepIdx;
                }
            });
        }
    }

    /**
     * @inheritDoc
     */
    public ngAfterViewInit() {
        const containers: TimelineNotificationContainerDirective[] = this.containerNotifications.toArray();

        containers.forEach((container: TimelineNotificationContainerDirective, index: number) => {
            this._notification.renderNotifications(this.circuit, this.circuit.workflow.tasks[index],
                container.viewContainerRef);
        });

        this._cdr.detectChanges();
    }

    /**
     * Fixes reject reason for invalid phone num case.
     *
     * @param reason the reference to the original reason.
     * @returns the fixed reason.
     */
    public getRejectReasonKey(reason: string): string {
        return this._dossierService.getRejectReasonKey(reason);
    }

    /**
     * Returns the title label for the specified status.
     *
     * @param {PesTaskStatus} status the status for which to return the label.
     * @returns {string} the title label for the specified status.
     */
    protected getStatusLabel(status: PesTaskStatus): string {
        let result: string = 'component.timeline.todo';
        if (status === PesTaskStatus.CANCELLED) {
            result = 'component.timeline.cancelled';
        } else if (status === PesTaskStatus.IN_PROGRESS) {
            result = 'component.timeline.next_step';
        } else if (status === PesTaskStatus.DONE) {
            result = 'component.timeline.done';
        }
        return this._translate.instant(result);
    }

    /**
     * Returns the icon associated with the specified status.
     *
     * @param {PesTaskStatus} status the status for which to return the icon.
     * @returns {string} he icon associated with the specified status.
     */
    protected getIcon(status: PesTaskStatus): string {
        let result: string = 'fa-clock-o';
        if (status === PesTaskStatus.CANCELLED || status === PesTaskStatus.REJECTED) {
            result = 'fa-times';
        } else if (status === PesTaskStatus.DONE || status === PesTaskStatus.SKIPPED ||
            status === PesTaskStatus.AUTO_SKIPPED) {
            result = 'fa-check';
        }
        return result;
    }

    /**
     * Indicates whether the specified task is displayed in the timeline (true), or not (false).
     *
     * @param {any} task the task to check.
     * @returns {boolean} a boolean that indicates whether the specified task is displayed in the timeline (true), or
     *                    not (false).
     */
    protected isTaskVisible(task: any): boolean {
        let result: boolean = true;
        if (this._hideIgnoredTasks && task.status!.code === PesTaskStatus.SKIPPED) {
            result = false;
        }
        if (result && this._excludedTasks && this._excludedTasks.indexOf(task.id) !== -1) {
            result = false;
        }
        return result;
    }

    /**
     * Sets all values defined in the component settings.
     */
    private setSettings(): void {
        const componentSetting: ComponentSetting = this._componentSetting.getSetting(this.circuit);
        if (componentSetting && componentSetting.timeline) {
            this._hideIgnoredTasks = componentSetting.timeline.hideIgnoredTasks;
            this._excludedTasks = componentSetting.timeline.excludedTasks;
        }
    }
}



// WEBPACK FOOTER //
// ./src/app/component/lbpa-timeline/lbpa-timeline.component.ts